import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSupabase } from './SupabaseContext';
import { useBusiness } from './BusinessContext';
import { toast } from 'react-hot-toast';
import { SupabaseClient } from '@supabase/supabase-js';

interface CallInfo {
  id: string;
  from: string;
  to: string;
  status: 'ringing' | 'in-progress' | 'completed' | 'failed' | 'busy' | 'no-answer' | 'canceled';
  direction: 'inbound' | 'outbound';
  businessId: string;
  createdAt: string;
  communication_id?: string;
  first_name?: string;
  last_name?: string;
  customer_phone?: string;
  customer_email?: string;
}

interface CallContextType {
  currentCall: CallInfo | null;
  callHistory: CallInfo[];
  answerCall: () => void;
  rejectCall: () => void;
  clearCurrentCall: () => void;
  setCurrentCall: (call: CallInfo | null) => void;
}

const CallContext = createContext<CallContextType | undefined>(undefined);

export function CallProvider({ children }: { children: React.ReactNode }) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const [currentCall, setCurrentCall] = useState<CallInfo | null>(null);
  const [callHistory, setCallHistory] = useState<CallInfo[]>([]);

  // Function to load recent call history
  useEffect(() => {
    if (!supabase || !selectedBusiness?.id) return;

    const loadCallHistory = async () => {
      try {
        const { data, error } = await supabase
          .from('twilio_calls')
          .select('*, communication_id, first_name, last_name, customer_phone, customer_email')
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) {
          console.error('Error loading call history:', error);
          return;
        }

        if (data) {
          const formattedCalls = data.map((call: any) => ({
            id: call.id,
            from: call.from_number,
            to: call.to_number,
            status: call.status,
            direction: call.direction,
            businessId: call.business_id,
            createdAt: call.created_at,
            communication_id: call.communication_id,
            first_name: call.first_name,
            last_name: call.last_name,
            customer_phone: call.customer_phone,
            customer_email: call.customer_email
          }));
          
          setCallHistory(formattedCalls);
        }
      } catch (error) {
        console.error('Failed to load call history:', error);
      }
    };

    loadCallHistory();
  }, [supabase, selectedBusiness?.id]);

  // Subscribe to call changes
  useEffect(() => {
    if (!supabase || !selectedBusiness?.id) return;

    console.log('Setting up call subscription for business ID:', selectedBusiness.id);

    // Subscribe to new calls
    const subscription = supabase
      .channel('twilio_calls_channel')
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'twilio_calls',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload: any) => {
          console.log('New call received via realtime:', payload);
          const newCall: CallInfo = {
            id: payload.new.id,
            from: payload.new.from_number,
            to: payload.new.to_number,
            status: payload.new.status,
            direction: payload.new.direction,
            businessId: payload.new.business_id,
            createdAt: payload.new.created_at,
            communication_id: payload.new.communication_id,
            first_name: payload.new.first_name,
            last_name: payload.new.last_name,
            customer_phone: payload.new.customer_phone,
            customer_email: payload.new.customer_email
          };

          // Add to call history
          setCallHistory(prev => [newCall, ...prev].slice(0, 10));

          // Set as current call if it's ringing and inbound
          if (newCall.status === 'ringing' && newCall.direction === 'inbound') {
            console.log('Showing notification for incoming call from:', newCall.from);
            
            // Get the latest call data with customer info before showing notification
            fetchCallWithCustomerInfo(newCall.id).then(updatedCall => {
              // If we got updated data with customer info, use it
              if (updatedCall) {
                setCurrentCall(updatedCall);
                showIncomingCallNotification(updatedCall);
              } else {
                // Otherwise use what we have
                setCurrentCall(newCall);
                showIncomingCallNotification(newCall);
              }
            }).catch(err => {
              // On error, fallback to using the original call data
              console.error('Error fetching call with customer info:', err);
              setCurrentCall(newCall);
              showIncomingCallNotification(newCall);
            });
          }
        }
      )
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'twilio_calls',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload: any) => {
          console.log('Call update received via realtime:', payload);
          const updatedCall: CallInfo = {
            id: payload.new.id,
            from: payload.new.from_number,
            to: payload.new.to_number,
            status: payload.new.status,
            direction: payload.new.direction,
            businessId: payload.new.business_id,
            createdAt: payload.new.created_at,
            communication_id: payload.new.communication_id,
            first_name: payload.new.first_name,
            last_name: payload.new.last_name,
            customer_phone: payload.new.customer_phone,
            customer_email: payload.new.customer_email
          };

          // Update in call history
          setCallHistory(prev => 
            prev.map(call => call.id === updatedCall.id ? updatedCall : call)
          );

          // Update current call if it's the same ID
          if (currentCall?.id === updatedCall.id) {
            if (['completed', 'failed', 'busy', 'no-answer', 'canceled'].includes(updatedCall.status)) {
              console.log(`Call ${updatedCall.id} status changed to ${updatedCall.status}, clearing notification`);
              // Stop ringtone
              stopRingtone();
              // Clear current call state
              setCurrentCall(null);
              // Dismiss the notification
              dismissCallNotification(updatedCall.id);
            } else {
              setCurrentCall(updatedCall);
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    return () => {
      console.log('Cleaning up call subscription');
      supabase.removeChannel(subscription);
    };
  }, [supabase, selectedBusiness?.id, currentCall]);

  // Show a toast notification for incoming calls
  const showIncomingCallNotification = (call: CallInfo) => {
    console.log('Attempting to show notification for call:', call);
    // Debug - log the full call details to check if customer info is present
    console.log('Call details for notification:', {
      id: call.id,
      from: call.from,
      communication_id: call.communication_id,
      first_name: call.first_name,
      last_name: call.last_name
    });

    // Format phone number for display
    const formattedPhoneNumber = formatPhoneNumber(call.from);
    
    // Determine caller display name with additional logging
    const hasCustomerInfo = Boolean(call.communication_id && call.first_name && call.last_name);
    console.log(`Call has customer info: ${hasCustomerInfo}`);
    
    const callerDisplay = hasCustomerInfo
      ? `${call.first_name} ${call.last_name}`
      : formattedPhoneNumber;
    
    console.log(`Will display caller as: "${callerDisplay}"`);
    
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex flex-col ring-1 ring-black ring-opacity-5 fixed top-4 left-4 z-[9999]`}
        >
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Incoming Call
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  From: {callerDisplay}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  onClick={() => {
                    answerCall();
                    toast.dismiss(t.id);
                  }}
                  className="bg-green-100 hover:bg-green-200 text-green-800 px-3 py-1 rounded-full mr-2"
                >
                  Answer
                </button>
                <button
                  onClick={() => {
                    rejectCall();
                    toast.dismiss(t.id);
                  }}
                  className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded-full"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
      { 
        duration: 30000, // Show for 30 seconds or until dismissed
        id: 'incoming-call-' + call.id, // Use a unique ID based on the call ID
      }
    );

    // Also play a ringtone
    playRingtone();
  };

  // Helper function to format phone numbers
  const formatPhoneNumber = (phoneNumber: string) => {
    // Basic US phone number formatting
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Remove country code for display
    const nationalNumber = cleaned.startsWith('1') ? cleaned.substring(1) : cleaned;
    
    if (nationalNumber.length === 10) {
      return `(${nationalNumber.substring(0, 3)}) ${nationalNumber.substring(3, 6)}-${nationalNumber.substring(6, 10)}`;
    }
    
    return phoneNumber;
  };

  // Play a ringtone sound
  const playRingtone = () => {
    try {
      const audio = new Audio('/sounds/ringtone.mp3');
      audio.loop = true;
      audio.play().catch(e => console.error('Could not play ringtone:', e));
      
      // Store the audio element to stop it later
      (window as any).ringtoneAudio = audio;

      // Stop after 30 seconds maximum
      setTimeout(() => {
        if ((window as any).ringtoneAudio) {
          (window as any).ringtoneAudio.pause();
          (window as any).ringtoneAudio = null;
        }
      }, 30000);
    } catch (error) {
      console.error('Error playing ringtone:', error);
    }
  };

  // Stop the ringtone
  const stopRingtone = () => {
    if ((window as any).ringtoneAudio) {
      (window as any).ringtoneAudio.pause();
      (window as any).ringtoneAudio = null;
    }
  };

  // Answer the current call
  const answerCall = async () => {
    if (!currentCall) return;
    
    stopRingtone();
    
    try {
      // Here you would typically implement call answering logic
      // For web-based implementation, you'd need to utilize Twilio Voice JS SDK
      // For now, we'll just update the status in our database
      
      if (supabase) {
        await supabase
          .from('twilio_calls')
          .update({ status: 'in-progress' })
          .eq('id', currentCall.id);
      }
      
      toast.success('Call answered');
    } catch (error) {
      console.error('Error answering call:', error);
      toast.error('Failed to answer call');
    }
  };

  // Reject the current call
  const rejectCall = async () => {
    if (!currentCall) return;
    
    stopRingtone();
    
    try {
      // Here you would typically implement call rejection logic
      // For now, we'll just update the status in our database
      
      if (supabase) {
        await supabase
          .from('twilio_calls')
          .update({ status: 'canceled' })
          .eq('id', currentCall.id);
      }
      
      setCurrentCall(null);
      toast.success('Call rejected');
    } catch (error) {
      console.error('Error rejecting call:', error);
      toast.error('Failed to reject call');
    }
  };

  // Clear the current call manually
  const clearCurrentCall = () => {
    stopRingtone();
    setCurrentCall(null);
  };

  // Function to get the latest call data with customer info
  const fetchCallWithCustomerInfo = async (callId: string): Promise<CallInfo | null> => {
    if (!supabase) return null;
    
    try {
      // Get the full call information including customer data
      const { data, error } = await supabase
        .from('twilio_calls')
        .select('*, communication_id, first_name, last_name, customer_phone, customer_email')
        .eq('id', callId)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching call with customer info:', error);
        return null;
      }
      
      if (!data) return null;
      
      console.log('Fetched call with customer info:', data);
      
      // Format to our CallInfo structure
      return {
        id: data.id,
        from: data.from_number,
        to: data.to_number,
        status: data.status,
        direction: data.direction,
        businessId: data.business_id,
        createdAt: data.created_at,
        communication_id: data.communication_id,
        first_name: data.first_name,
        last_name: data.last_name,
        customer_phone: data.customer_phone,
        customer_email: data.customer_email
      };
    } catch (err) {
      console.error('Error in fetchCallWithCustomerInfo:', err);
      return null;
    }
  };

  // Function to dismiss a call notification by ID
  const dismissCallNotification = (callId: string) => {
    // Dismiss the toast notification
    toast.dismiss(`incoming-call-${callId}`);
  };

  // Value to be provided by the context
  const value = {
    currentCall,
    callHistory,
    answerCall,
    rejectCall,
    clearCurrentCall,
    setCurrentCall
  };

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
}

// Hook to use the Call context
export function useCall() {
  const context = useContext(CallContext);
  
  if (context === undefined) {
    throw new Error('useCall must be used within a CallProvider');
  }
  
  return context;
} 